// @systeme: interface
// Créer une récurrence en trois champs : quoi, quand, quelle catégorie (elle décide des
// rappels). On part d'un modèle du catalogue ou de rien.
import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { ChevronLeft } from 'lucide-react';
import { Link as LienRouteur, useNavigate } from 'react-router';
import type { CategorieElement, ModeleRecurrence, RegleRecurrence } from '@foyer/shared';
import { Chargement } from '../composants/Chargement';
import { useMessages } from '../composants/Messages';
import { api } from '../lib/api';
import { tailles } from '../theme/theme';

type Cadence = 'jour' | 'semaine' | 'quinzaine' | 'mois' | 'annee';

const CADENCES: { valeur: Cadence; libelle: string }[] = [
  { valeur: 'semaine', libelle: 'Chaque semaine' },
  { valeur: 'quinzaine', libelle: 'Une semaine sur deux' },
  { valeur: 'mois', libelle: 'Chaque mois' },
  { valeur: 'annee', libelle: 'Chaque année' },
  { valeur: 'jour', libelle: 'Chaque jour' },
];
const JOURS = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];
const NOMS_JOURS = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'];
const MOIS = [
  'janvier',
  'février',
  'mars',
  'avril',
  'mai',
  'juin',
  'juillet',
  'août',
  'septembre',
  'octobre',
  'novembre',
  'décembre',
];
const CATEGORIES: { valeur: CategorieElement; libelle: string; rappel: string }[] = [
  { valeur: 'poubelles', libelle: 'Poubelles', rappel: 'rappel la veille à 19h' },
  { valeur: 'finances', libelle: 'Facture, paiement', rappel: 'rappel 5 jours avant' },
  { valeur: 'maison', libelle: 'Maison', rappel: 'rappel le matin même' },
  { valeur: 'administratif', libelle: 'Papiers', rappel: 'rappels 7 jours et 2 jours avant' },
  { valeur: 'sante', libelle: 'Santé', rappel: 'rappel la veille à 18h' },
  { valeur: 'scolaire', libelle: 'École', rappel: 'rappel la veille à 18h' },
  { valeur: 'social', libelle: 'Famille, amis', rappel: 'rappel la veille à 18h' },
  { valeur: 'autre', libelle: 'Autre', rappel: 'rappel le matin même' },
];
const FAMILLES: { cle: ModeleRecurrence['famille']; libelle: string }[] = [
  { cle: 'poubelles', libelle: 'Poubelles' },
  { cle: 'factures', libelle: 'Factures' },
  { cle: 'maison', libelle: 'Maison' },
  { cle: 'vehicule', libelle: 'Voiture' },
  { cle: 'assurances', libelle: 'Assurances' },
];

function cadenceDe(regle: ModeleRecurrence['regle']): Cadence {
  if (regle.frequence === 'quotidien') return 'jour';
  if (regle.frequence === 'hebdomadaire') return regle.intervalle === 2 ? 'quinzaine' : 'semaine';
  if (regle.frequence === 'mensuel') return 'mois';
  return 'annee';
}

export function NouvelleRecurrence() {
  const navigate = useNavigate();
  const { dire, direErreur } = useMessages();
  const [catalogue, setCatalogue] = useState<ModeleRecurrence[] | undefined>();
  const [aujourdhui, setAujourdhui] = useState<string | undefined>();
  const [etape, setEtape] = useState<'choix' | 'champs'>('choix');
  const [titre, setTitre] = useState('');
  const [cadence, setCadence] = useState<Cadence>('semaine');
  const [joursSemaine, setJoursSemaine] = useState<number[]>([]);
  const [jourMois, setJourMois] = useState(1);
  const [mois, setMois] = useState(1);
  const [jourAnnee, setJourAnnee] = useState(1);
  const [avecHeure, setAvecHeure] = useState(false);
  const [heure, setHeure] = useState('');
  const [categorie, setCategorie] = useState<CategorieElement>('autre');
  const [enCours, setEnCours] = useState(false);

  useEffect(() => {
    api.recurrences.catalogue().then(setCatalogue).catch(direErreur);
    api.temps
      .aujourdhui()
      .then((t) => setAujourdhui(t.jour))
      .catch(direErreur);
  }, [direErreur]);

  function partirDe(modele?: ModeleRecurrence) {
    if (modele) {
      setTitre(modele.gabarit.titre);
      setCategorie(modele.gabarit.categorie ?? 'autre');
      setCadence(cadenceDe(modele.regle));
      setJoursSemaine(modele.regle.joursSemaine ?? []);
      setJourMois(typeof modele.regle.jourMois === 'number' ? modele.regle.jourMois : 1);
      setMois(modele.regle.mois ?? 1);
      setJourAnnee(modele.regle.jour ?? 1);
      setAvecHeure(Boolean(modele.regle.heure));
      setHeure(modele.regle.heure ?? '');
    }
    setEtape('champs');
  }

  const pret =
    titre.trim().length > 0 &&
    (cadence !== 'semaine' && cadence !== 'quinzaine' ? true : joursSemaine.length > 0) &&
    (!avecHeure || heure !== '') &&
    aujourdhui !== undefined;

  function regle(): RegleRecurrence | undefined {
    if (!aujourdhui) return undefined;
    const base = { heure: avecHeure && heure ? heure : null, debut: aujourdhui, fin: null };
    switch (cadence) {
      case 'jour':
        return { frequence: 'quotidien', intervalle: 1, ...base };
      case 'semaine':
        return { frequence: 'hebdomadaire', intervalle: 1, joursSemaine, ...base };
      case 'quinzaine':
        return { frequence: 'hebdomadaire', intervalle: 2, joursSemaine, ...base };
      case 'mois':
        return { frequence: 'mensuel', intervalle: 1, jourMois, ...base };
      case 'annee':
        return { frequence: 'annuel', intervalle: 1, mois, jour: jourAnnee, ...base };
    }
  }

  async function enregistrer() {
    const r = regle();
    if (!pret || !r) return;
    setEnCours(true);
    try {
      await api.recurrences.creer({
        gabarit: {
          titre: titre.trim(),
          type: 'tache',
          categorie,
          detail: null,
          visibilite: 'espace',
          sensibilite: 'normale',
          personneId: null,
        },
        regle: r,
      });
      dire('C’est noté. Tu n’y penses plus.');
      void navigate('/a-venir/recurrences');
    } catch (e) {
      direErreur(e);
    } finally {
      setEnCours(false);
    }
  }

  if (!catalogue) return <Chargement />;

  const rappel = CATEGORIES.find((c) => c.valeur === categorie)?.rappel;

  return (
    <Box sx={{ px: `${tailles.marge}px`, pt: 3, pb: 4 }}>
      <Button
        component={LienRouteur}
        to="/a-venir/recurrences"
        startIcon={<ChevronLeft size={18} />}
        color="inherit"
        sx={{ px: 0, mb: 1 }}
      >
        Ce qui revient
      </Button>

      {etape === 'choix' ? (
        <>
          <Typography variant="h1" component="h1" sx={{ mb: 2 }}>
            Qu’est-ce qui revient ?
          </Typography>
          {FAMILLES.map((f) => (
            <Box key={f.cle} sx={{ mb: 2 }}>
              <Typography variant="h3" component="h2" sx={{ mb: 1 }}>
                {f.libelle}
              </Typography>
              <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1}>
                {catalogue
                  .filter((m) => m.famille === f.cle)
                  .map((m) => (
                    <Chip
                      key={m.cle}
                      label={m.nom}
                      onClick={() => partirDe(m)}
                      variant="outlined"
                    />
                  ))}
              </Stack>
            </Box>
          ))}
          <Button variant="contained" onClick={() => partirDe()} sx={{ mt: 1 }}>
            Autre chose
          </Button>
        </>
      ) : (
        <Box
          component="form"
          onSubmit={(e) => {
            e.preventDefault();
            void enregistrer();
          }}
        >
          <Typography variant="h1" component="h1" sx={{ mb: 2 }}>
            Trois choses à dire
          </Typography>
          <Stack spacing={3}>
            <TextField
              label="Quoi ?"
              value={titre}
              onChange={(e) => setTitre(e.target.value)}
              autoFocus
              inputProps={{ maxLength: 200 }}
            />

            <Box>
              <Typography variant="h3" component="h2" sx={{ mb: 1 }}>
                Quand ?
              </Typography>
              <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1} sx={{ mb: 1.5 }}>
                {CADENCES.map((c) => (
                  <Chip
                    key={c.valeur}
                    label={c.libelle}
                    color={cadence === c.valeur ? 'primary' : 'default'}
                    variant={cadence === c.valeur ? 'filled' : 'outlined'}
                    onClick={() => setCadence(c.valeur)}
                  />
                ))}
              </Stack>
              {(cadence === 'semaine' || cadence === 'quinzaine') && (
                <ToggleButtonGroup
                  value={joursSemaine}
                  onChange={(_e, v: number[]) => setJoursSemaine(v)}
                  fullWidth
                  aria-label="Jours de la semaine"
                >
                  {JOURS.map((j, i) => (
                    <ToggleButton key={i} value={i + 1} aria-label={NOMS_JOURS[i]}>
                      {j}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              )}
              {cadence === 'mois' && (
                <TextField
                  select
                  label="Quel jour du mois ?"
                  value={jourMois}
                  onChange={(e) => setJourMois(Number(e.target.value))}
                  sx={{ maxWidth: 220 }}
                >
                  {Array.from({ length: 31 }, (_v, i) => i + 1).map((j) => (
                    <MenuItem key={j} value={j}>
                      {j === 1 ? 'Le 1er' : `Le ${j}`}
                    </MenuItem>
                  ))}
                </TextField>
              )}
              {cadence === 'annee' && (
                <Stack direction="row" spacing={1}>
                  <TextField
                    select
                    label="Jour"
                    value={jourAnnee}
                    onChange={(e) => setJourAnnee(Number(e.target.value))}
                    sx={{ width: 110 }}
                  >
                    {Array.from({ length: 31 }, (_v, i) => i + 1).map((j) => (
                      <MenuItem key={j} value={j}>
                        {j}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    label="Mois"
                    value={mois}
                    onChange={(e) => setMois(Number(e.target.value))}
                    fullWidth
                  >
                    {MOIS.map((m, i) => (
                      <MenuItem key={m} value={i + 1}>
                        {m}
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>
              )}
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1 }}>
                <FormControlLabel
                  control={
                    <Switch checked={avecHeure} onChange={(e) => setAvecHeure(e.target.checked)} />
                  }
                  label="À une heure précise"
                />
                {avecHeure && (
                  <TextField
                    type="time"
                    value={heure}
                    onChange={(e) => setHeure(e.target.value)}
                    inputProps={{ 'aria-label': 'Heure' }}
                    sx={{ maxWidth: 140 }}
                  />
                )}
              </Stack>
            </Box>

            <Box>
              <Typography variant="h3" component="h2" sx={{ mb: 1 }}>
                C’est quel genre de chose ?
              </Typography>
              <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1}>
                {CATEGORIES.map((c) => (
                  <Chip
                    key={c.valeur}
                    label={c.libelle}
                    color={categorie === c.valeur ? 'primary' : 'default'}
                    variant={categorie === c.valeur ? 'filled' : 'outlined'}
                    onClick={() => setCategorie(c.valeur)}
                  />
                ))}
              </Stack>
              {rappel && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Je prévois un {rappel}.
                </Typography>
              )}
            </Box>

            <Button type="submit" variant="contained" size="large" disabled={!pret || enCours}>
              C’est parti
            </Button>
          </Stack>
        </Box>
      )}
    </Box>
  );
}
